import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import useFetch from "hooks/useFetch";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import MutateProjectMenu from "./MutateProjectMenu";
import _orderBy from "lodash/orderBy";
import { useState, useMemo } from "react";

export default function ProjectList() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const { data: projectRes } = useFetch("/project");

  const projects = !customerId
    ? projectRes?.projects
    : projectRes?.projects.filter((p) => p.customer_id === Number(customerId));

  const onRequestSort = (field) => () => {
    const isAsc = orderBy === field && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
  };

  const filteredProjects = useMemo(() => {
    return projects?.filter((p) =>
      p.name.toLowerCase().includes(query.toLowerCase())
    );
  }, [projects, query]);

  const sortedProjects = _orderBy(filteredProjects || [], [orderBy], [order]);

  return (
    <>
      <TextField
        placeholder="Search projects"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        variant="outlined"
        sx={{ mb: 2, width: 300 }}
      />
      <TableContainer
        component={Paper}
        sx={{ minWidth: 650, maxHeight: "calc(100vh - 130px)" }}
        className="xyloplan-scrollbar"
      >
        <Table stickyHeader aria-label="projects table">
          <TableHead>
            <TableRow>
              {[
                { id: "name", label: "Name" },
                { id: "mtt_count", label: "Vegetation Simulations" },
                { id: "failed_mtt_count", label: "Veg Failed / In Progress" },
                { id: "intermix_count", label: "Urban Simulations" },
                {
                  id: "failed_intermix_count",
                  label: "Urban Failed / In Progress",
                },
                { id: "updated_at", label: "Last Modified" },
              ].map((headCell) => (
                <EnhancedTableHeadCell
                  key={headCell.id}
                  headCell={headCell}
                  orderBy={orderBy}
                  order={orderBy === headCell.id ? order : "asc"}
                  onRequestSort={onRequestSort(headCell.id)}
                />
              ))}
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedProjects.map((project) => (
              <TableRow
                hover
                key={project.id}
                onClick={() => navigate(`/project/${project.id}`)}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  cursor: "pointer",
                }}
              >
                <TableCell>{`${project.name} (${project.id})`}</TableCell>
                <TableCell align="left">{project.mtt_count}</TableCell>
                <TableCell align="left">
                  <Typography
                    color="error"
                    as="span"
                    sx={{ fontSize: "inherit" }}
                  >
                    {project.failed_mtt_count}
                  </Typography>{" "}
                  / {project.running_mtt_count}
                </TableCell>
                <TableCell align="left">{project.intermix_count}</TableCell>
                <TableCell align="left" display="inline-flex">
                  <Typography
                    color="error"
                    as="span"
                    sx={{ fontSize: "inherit" }}
                  >
                    {project.failed_intermix_count}
                  </Typography>{" "}
                  / {project.running_intermix_count}
                </TableCell>
                <TableCell align="left">
                  {dayjs(project.updated_at).format("MMM D, YYYY")}
                </TableCell>
                <TableCell align="left">
                  <MutateProjectMenu project={project} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

function EnhancedTableHeadCell(props) {
  const { order, orderBy, headCell, onRequestSort } = props;
  return (
    <TableCell sortDirection={orderBy === headCell.id ? order : false}>
      <TableSortLabel
        align="left"
        active={orderBy === headCell.id}
        direction={orderBy === headCell.id ? order : "asc"}
        onClick={onRequestSort}
      >
        {headCell.label}
      </TableSortLabel>
    </TableCell>
  );
}
